/**
 * Jeu de données minimal pour simuler le flux pharmacie H3.
 * 4 ordonnances pré-envoyées · 3 patients · scénarios assuré / payeur / rupture.
 */
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { insurerCodeToOrgId } from "@itaas/insurance-contract";
import {
  addPrescriptionLine,
  createConsultation,
  getPrescriptionByConsultation,
  listConsultations,
  listPrescriptions,
  markPrescriptionSent,
} from "@/core/clinical/clinical-store";
import type { PrescriptionLineInput } from "@/core/clinical/types";
import { IAM_TENANT_SLUG } from "@/core/config";
import { isDemoModeEnabled } from "@/core/demo/is-demo-mode-enabled";
import { createCoverageCase, reviewCoverageCase } from "@/core/insurance/coverage-case-store";
import { listInsurers } from "@/core/insurance/insurer-store";
import {
  getActivePartnershipForInsurer,
  type HospitalPartnership,
} from "@/core/insurance/partnership-store";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import type { StockProduct } from "@/core/pharmacy/types";
import { listPatients, updatePatient } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";

const SEED_KEY = "hospitol.pharmacyFulfillment.demoSeeded";

export const DEMO_RX_TAGS = {
  cash: "[DÉMO-RX-A]",
  insurerOk: "[DÉMO-RX-B]",
  insurerPartial: "[DÉMO-RX-C]",
  shortage: "[DÉMO-RX-D]",
} as const;

const PRODUCT_HINTS = {
  paracetamol: ["sku-med-003", "med-003", "paracétamol", "paracetamol"],
  infliximab: ["sku-med-010", "med-010", "infliximab"],
  amoxicillin: ["sku-med-006", "med-006", "amoxicilline", "amoxicillin"],
} as const;

function findProduct(
  products: StockProduct[],
  kind: keyof typeof PRODUCT_HINTS,
): StockProduct | undefined {
  const hints = PRODUCT_HINTS[kind];
  return (
    products.find((p) =>
      hints.some(
        (h) =>
          p.sku.toLowerCase() === h ||
          p.code.toLowerCase() === h ||
          p.label.toLowerCase().includes(h),
      ),
    ) ?? products[0]
  );
}

function hasDemoPrescription(tag: string): boolean {
  const consultations = listConsultations();
  return listPrescriptions().some((rx) => {
    const con = consultations.find((c) => c.id === rx.consultationId);
    return con?.motif.includes(tag) ?? false;
  });
}

function ensureDemoPartnership(insurerId: string, insurerCode: string): void {
  if (getActivePartnershipForInsurer(insurerId)) return;
  const session = loadStoredSession();
  const now = new Date().toISOString();
  const partnership: HospitalPartnership = {
    id: `part-demo-${insurerCode.toLowerCase()}`,
    insurerOrgId: insurerCodeToOrgId(insurerCode),
    insurerId,
    hospitalTenantId: session?.tenant?.id ?? "tenant-demo-hospitol",
    hospitalTenantSlug: session?.tenant?.slug ?? IAM_TENANT_SLUG,
    hospitalName: session?.tenant?.name ?? "Centre Médical Salama",
    status: "active",
    requestId: "demo-seed",
    approvedAt: now,
    createdAt: now,
    approvalCode: "DEMO-APPROVED",
  };
  const raw = localStorage.getItem("hospitol.insurancePartnerships");
  const list: HospitalPartnership[] = raw ? (JSON.parse(raw) as HospitalPartnership[]) : [];
  list.unshift(partnership);
  localStorage.setItem("hospitol.insurancePartnerships", JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:insurance-partnerships"));
}

function lineInput(product: StockProduct, quantity: number): PrescriptionLineInput {
  return {
    productId: product.id,
    productLabel: product.label,
    quantity,
    frequency: "twice_daily",
    durationDays: 5,
    timing: ["morning", "evening"],
    instructions: "Démo dispensation",
  };
}

function createSentPrescription(input: {
  patientId: string;
  motif: string;
  diagnostic: string;
  lines: PrescriptionLineInput[];
}): string | null {
  const consultation = createConsultation({
    patientId: input.patientId,
    motif: input.motif,
    diagnostic: input.diagnostic,
    acts: "Ordonnance démo — file pharmacie",
  });
  const prescription = getPrescriptionByConsultation(consultation.id);
  if (!prescription) return null;

  for (const line of input.lines) {
    addPrescriptionLine(prescription.id, line);
  }
  markPrescriptionSent(prescription.id);
  return prescription.id;
}

function patchInsuredPatient(
  patientId: string,
  insurerId: string,
  insurerName: string,
): void {
  const patient = listPatients().find((p) => p.id === patientId);
  if (!patient) return;
  updatePatient(patientId, {
    firstName: patient.firstName,
    lastName: patient.lastName,
    gender: patient.gender,
    patientType: patient.patientType,
    phone: patient.phone,
    address: patient.address,
    birthDate: patient.birthDate,
    coverageType: "insurer",
    coverageName: insurerName,
    insurerId,
    memberNumber: "MB-DEMO-001",
    policyNumber: "POL-DEMO-2026",
  });
}

/**
 * Crée 4 ordonnances en file pharmacie (tenant demo-hospitol uniquement).
 *
 * | Tag | Patient | Scénario |
 * |-----|---------|----------|
 * | RX-A | Jean | Payeur patient → caisse → livrer |
 * | RX-B | Marie | Assuré → facture backlog → livrer sans paiement |
 * | RX-C | Paul | Para OK + Infliximab validation en attente |
 * | RX-D | Jean | Rupture stock (qté élevée) → impression reste |
 */
export async function seedPharmacyFulfillmentDemo(): Promise<void> {
  if (!isDemoModeEnabled()) return;
  const tenantSlug = loadStoredSession()?.tenant?.slug ?? IAM_TENANT_SLUG;
  if (tenantSlug !== "demo-hospitol") return;
  if (localStorage.getItem(SEED_KEY)) return;

  const patients = listPatients();
  const jean = patients.find((p) => p.lastName === "Mukendi");
  const marie = patients.find((p) => p.lastName === "Kabila");
  const paul = patients.find((p) => p.lastName === "Ilunga");
  if (!jean || !marie || !paul) return;

  let products: StockProduct[] = [];
  try {
    products = (await pharmacyInventoryApi.listPharmacyProducts()).filter(
      (p) => p.status === "ACTIVE",
    );
  } catch {
    return;
  }
  if (products.length === 0) return;

  const para = findProduct(products, "paracetamol");
  const inflix = findProduct(products, "infliximab");
  const amox = findProduct(products, "amoxicillin") ?? para;
  if (!para) return;

  const insurers = listInsurers(true);
  const mutuelle = insurers.find((i) => i.code === "MUT-SALAMA") ?? insurers[0];
  if (mutuelle) {
    ensureDemoPartnership(mutuelle.id, mutuelle.code);
    patchInsuredPatient(marie.id, mutuelle.id, mutuelle.name);
    patchInsuredPatient(paul.id, mutuelle.id, mutuelle.name);
  }

  if (!hasDemoPrescription(DEMO_RX_TAGS.cash)) {
    createSentPrescription({
      patientId: jean.id,
      motif: `${DEMO_RX_TAGS.cash} Fièvre — payeur patient`,
      diagnostic: "Syndrome grippal — démo caisse",
      lines: [lineInput(para, 2)],
    });
  }

  if (!hasDemoPrescription(DEMO_RX_TAGS.insurerOk) && mutuelle) {
    const rxId = createSentPrescription({
      patientId: marie.id,
      motif: `${DEMO_RX_TAGS.insurerOk} Assuré sans validation médicament`,
      diagnostic: "Douleurs légères — tiers payant direct",
      lines: [lineInput(para, 1)],
    });
    if (rxId) {
      const cc = createCoverageCase({
        insurerId: mutuelle.id,
        patientId: marie.id,
        prescriptionId: rxId,
        kind: "prescription",
        label: "Ordonnance démo — validée",
        amount: 2500,
        memberNumber: "MB-DEMO-001",
        policyNumber: "POL-DEMO-2026",
      });
      if (cc) reviewCoverageCase(cc.id, "approved", "Démo seed");
    }
  }

  if (!hasDemoPrescription(DEMO_RX_TAGS.insurerPartial) && mutuelle && inflix) {
    const rxId = createSentPrescription({
      patientId: paul.id,
      motif: `${DEMO_RX_TAGS.insurerPartial} Para + accord préalable`,
      diagnostic: "Polyarthrite — démo validation partielle",
      lines: [lineInput(para, 1), lineInput(inflix, 1)],
    });
    if (rxId) {
      createCoverageCase({
        insurerId: mutuelle.id,
        patientId: paul.id,
        prescriptionId: rxId,
        kind: "prescription",
        label: "Ordonnance démo — Infliximab en attente",
        amount: 450_000,
        memberNumber: "MB-DEMO-002",
        policyNumber: "POL-DEMO-2026",
      });
    }
  }

  if (!hasDemoPrescription(DEMO_RX_TAGS.shortage) && amox) {
    createSentPrescription({
      patientId: jean.id,
      motif: `${DEMO_RX_TAGS.shortage} Rupture stock simulée`,
      diagnostic: "Quantité > stock disponible",
      lines: [lineInput(amox, 99)],
    });
  }

  localStorage.setItem(SEED_KEY, new Date().toISOString());

  console.info(
    "[Hospitol démo] Flux pharmacie prêt —",
    Object.values(DEMO_RX_TAGS).join(", "),
    "· Patients:",
    [jean, marie, paul].map((p) => patientFullName(p)).join(", "),
  );
}

export function resetPharmacyFulfillmentDemo(): void {
  localStorage.removeItem(SEED_KEY);
}

/** Résumé des scénarios pour affichage UI. */
export function getPharmacyFulfillmentDemoGuide(): Array<{
  tag: string;
  patient: string;
  steps: string;
}> {
  const patients = listPatients();
  const jean = patients.find((p) => p.lastName === "Mukendi");
  const marie = patients.find((p) => p.lastName === "Kabila");
  const paul = patients.find((p) => p.lastName === "Ilunga");
  return [
    {
      tag: DEMO_RX_TAGS.cash,
      patient: jean ? patientFullName(jean) : "Jean",
      steps: "Valider stock → Comptabilité → Caisse (payer) → Livrer",
    },
    {
      tag: DEMO_RX_TAGS.insurerOk,
      patient: marie ? patientFullName(marie) : "Marie",
      steps: "Valider stock → Comptabilité (assureur émis) → Livrer",
    },
    {
      tag: DEMO_RX_TAGS.insurerPartial,
      patient: paul ? patientFullName(paul) : "Paul",
      steps: "Para seul en compta · Infliximab bloqué → valider dans Assurance",
    },
    {
      tag: DEMO_RX_TAGS.shortage,
      patient: jean ? patientFullName(jean) : "Jean",
      steps: "Valider stock (rupture) → Imprimer ordonnance restante",
    },
  ];
}
